import 'componentsV2/OTPTransfer/OTPTransfer.scss';

import React, { useRef, useState, useEffect } from 'react';
import { FormattedText, formatText } from 'core/i18n';
import UtagHelper from 'utilities/utagHelper';
import { checkConditionAndExecuteCallback } from 'utilities/toggling';
import MessagesScreen from 'components/messagesScreen/messagesScreen';
import OtpTransferComponent from 'componentsV2/OTPTransfer/OTPTransfer.jsx';
import OTPTransferHelpModal from 'componentsV2/OTPTransfer/OTPTransferHelpModal.jsx';
import OTPAlternativeOptionModal from 'componentsV2/OTPTransfer/OTPAlternativeOptionModal.jsx';
import OtpTransferAlternativeOption from 'componentsV2/OTPTransfer/OtpTransferAlternativeOption';
import { welcomeCard as WelcomeCard } from 'components/welcomeCard/welcomeCard';
import { deleteCookieAndGoHome } from 'utilities/logoutHelper';
import { RoadmapNavigation } from 'components/steps/roadmapNavigation';
import { STAND_BY, SUCCESS, ACTIVATED, DOCUMENTATION } from 'constants/statusProcess';
import { useOTPTransferData } from './hooks/useTransferOTPData';
import { useFetchUserStatus } from './hooks/useFetchUserStatus';
import {
    doTagCheckAndRecordWhichTagForSuccessStatus,
    doTagCheckBoxAlternativeOption,
    doTagContinueButton,
    doTagOnPageReload,
    doTagShowHelpModal,
    doTagSuccessfulEvent,
    doTagToggleAlternativeOptionModal,
} from './tagging';

export const OTPTransfer = ({
    change,
    changeCurrentStep,
    changeUserStatusCall,
    enableFinishedAndNextStep,
    finalizeStep,
    hidePreLoader,
    ibanTransferOTPCall,
    immErrorState,
    immStepsState,
    products,
    showPreLoader,
    userProfile,
}) => {
    const [isDisabled, setIsDisabled] = useState(true);
    const [transferEventRecorded, setTransferEventRecorded] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showAlternativeOption, setShowAlternativeOption] = useState(false);
    const [checkBoxAlternativeOption, setCheckBoxAlternativeOption] = useState(false);
    const [showAlternativeOptionModal, setShowAlternativeOptionModal] = useState(false);

    const alternativeOptionRef = useRef(null);

    const { OTPTransferData, setOTPTransferData, fetchOTPTransferData } = useOTPTransferData({
        ibanTransferOTPCall,
        userProfile,
    });

    useFetchUserStatus({
        changeUserStatusCall,
        fetchOTPTransferData,
        hidePreLoader,
        ibanTransferOTPCall,
        showPreLoader,
        userProfile,
    });

    useEffect(() => {
        if (showAlternativeOption) {
            alternativeOptionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showAlternativeOption]);

    const handleShowHelpModal = event => {
        event.preventDefault();
        setShowHelpModal(true);
        doTagShowHelpModal();
    };

    const handleHideHelpModal = event => {
        event.preventDefault();
        setShowHelpModal(false);
    };

    const handleShowAlternativeOption = event => {
        event.preventDefault();
        setShowAlternativeOption(true);
    };

    const handleHideAlternativeOptionContainer = () => {
        setShowAlternativeOption(false);
    };

    const toggleAlternativeOptionModal = () => {
        setShowAlternativeOptionModal(prevState => !prevState);
        doTagToggleAlternativeOptionModal(userProfile.investmentType);
    };

    const handleCheckBoxAlternativeOption = event => {
        event.preventDefault();
        setCheckBoxAlternativeOption(prevState => !prevState);
        const tagEvent = checkBoxAlternativeOption ? 'uncheck-transferencia' : 'check-transferencia';
        doTagCheckBoxAlternativeOption(tagEvent);
    };

    const finishAlternativeOptionStep = () => {
        toggleAlternativeOptionModal();
    };

    useEffect(() => {
        const jointHolderInfo = UtagHelper.getJointHolderInfoFromUserProfile(userProfile);
        if (!immStepsState.get('welcomeCard') && !transferEventRecorded) {
            doTagOnPageReload({
                encryptedDocNumber: userProfile.encryptedDocNumber,
                identityMethod: userProfile.identityMethod,
                investmentType: userProfile.investmentType,
                jointHolderInfo,
                product: userProfile.product,
                productsDescription: products.get('productsDescription'),
                promoCode: userProfile.promoCode,
                recordId: userProfile.recordId,
                userId: userProfile.userId,
            });
        }

        setTransferEventRecorded(true);
    }, [userProfile, immStepsState, products, transferEventRecorded]);

    const getBatchMessages = () => {
        hidePreLoader();

        return (
            <MessagesScreen
                iconClass="icons icons--icon-clock-type-2"
                title={formatText('messagesScreen-batchErrorTitle')}
                subtitle={formatText('messagesScreen-batchErrorSubTitle')}
                buttonText={formatText('button-understood')}
                buttonClass="form__button form__button-secondary"
                action={deleteCookieAndGoHome}
            >
                <FormattedText value="messagesScreen-batchErrorContent" />
                <FormattedText value="messagesScreen-batchErrorContent2" />
                <FormattedText value="messagesScreen-customerServiceDepartment" />
            </MessagesScreen>
        );
    };

    const onHandleDisableNextButton = value => setIsDisabled(value);

    const showPreLoaderAndDisableButton = () => {
        showPreLoader();
        onHandleDisableNextButton(true);
    };

    const finalizeCurrentStep = () => {
        const currentStep = immStepsState.get('currentStep');
        finalizeStep({
            index: currentStep,
            finished: true,
        });

        setTimeout(() => {
            const jointHolderInfo = UtagHelper.getJointHolderInfoFromUserProfile(userProfile);

            if (!immStepsState.get('welcomeCard')) {
                doTagCheckAndRecordWhichTagForSuccessStatus({
                    channel: userProfile.channel,
                    clientRisk: userProfile.clientRisk,
                    docNumber: userProfile.docNumber,
                    documentationStatusCounter: userProfile.documentationStatusCounter,
                    identityMethod: userProfile.identityMethod,
                    immStepsState,
                    investmentType: userProfile.investmentType,
                    jointHolderInfo,
                    product: userProfile.product,
                    products,
                    promoCode: userProfile.promoCode,
                    recordId: userProfile.recordId,
                    userId: userProfile.userId,
                });
            }

            enableFinishedAndNextStep();
        }, 200);
    };

    const handleDocumentationStatus = () => {
        change('userProfile.documentationUploaded', false);
        changeCurrentStep({ index: 5 });
    };

    const handleUserStatusChange = status => {
        change('userProfile.status', status);
        const userIsActivatedOrStandBy = [SUCCESS, STAND_BY, ACTIVATED].includes(status);

        checkConditionAndExecuteCallback(userIsActivatedOrStandBy, finalizeCurrentStep);
        checkConditionAndExecuteCallback(status === DOCUMENTATION, handleDocumentationStatus);
    };

    const handleTaggingAndEvents = () => {
        setTimeout(() => {
            hidePreLoader();
        }, 200);

        doTagContinueButton();
        doTagSuccessfulEvent(userProfile.investmentType);
    };

    const finishStep = () => {
        showPreLoaderAndDisableButton();

        changeUserStatusCall(userProfile.accessToken, 'RETURN_STATUS').then(({ status }) => {
            handleUserStatusChange(status);
            handleTaggingAndEvents();
        });
    };

    const isAlternativeOptionButtonDisabled = () => !checkBoxAlternativeOption;

    if (immErrorState.get('batchError')) return <div className="profesional-data-view">{getBatchMessages()}</div>;

    const { date, retryTransfer, iban, entityName } = OTPTransferData;

    return (
        <>
            <div className="prospects-step-content basic-layering">
                <OtpTransferComponent
                    otpDate={date}
                    iban={iban}
                    retyTransfer={retryTransfer}
                    entityName={entityName}
                    onHandleDisableNextButton={onHandleDisableNextButton}
                    onHandleSetOtpTransferData={setOTPTransferData}
                    showHelpModal={handleShowHelpModal}
                    showAlternativeOption={handleShowAlternativeOption}
                    hideAlternativeOptionModal={handleHideAlternativeOptionContainer}
                    investmentType={userProfile.investmentType}
                />
                {!showAlternativeOption && (
                    <div className="row form__footer">
                        <div className="col-lg-12">
                            <RoadmapNavigation
                                buttonPrevSpecialHidden={true}
                                buttonPrevHidden={true}
                                buttonContinueAction={finishStep}
                                buttonContinueDisabled={isDisabled}
                            />
                        </div>
                    </div>
                )}
            </div>

            {showAlternativeOption && (
                <div ref={alternativeOptionRef}>
                    <OtpTransferAlternativeOption
                        userIban={userProfile.iban}
                        iban={OTPTransferData.iban}
                        entityName={OTPTransferData.entityName}
                        userProfile={userProfile}
                        checkBoxAlternativeOption={checkBoxAlternativeOption}
                        handleCheckBoxAlternativeOption={handleCheckBoxAlternativeOption}
                    />
                    <div className="row form__footer">
                        <div className="col-lg-12">
                            <RoadmapNavigation
                                buttonPrevSpecialHidden={true}
                                buttonPrevHidden={true}
                                buttonContinueAction={toggleAlternativeOptionModal}
                                buttonContinueDisabled={isAlternativeOptionButtonDisabled()}
                            />
                        </div>
                    </div>
                </div>
            )}

            <WelcomeCard />
            <OTPTransferHelpModal showModal={showHelpModal} hideModal={handleHideHelpModal} />
            {showAlternativeOptionModal && (
                <OTPAlternativeOptionModal
                    showModal={showAlternativeOptionModal}
                    hideModal={toggleAlternativeOptionModal}
                    finishStep={finishAlternativeOptionStep}
                />
            )}
        </>
    );
};
